import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Container,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";

const SOURCE_OPTIONS = [
  'OTR',
  'CDL Consultant',
  'CDL Legal',
  'TVC',
  'LRIS',
  'AVVO',
  'Simplex',
  'ARAG',
  'US Legal',
  'Texas Legal',
  'Legal Shield',
  'Repeat',
  'Google',
  'Other',
];
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import ManagerApi from "../../api/ManagerApi";
import UserDocumentApi, {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_BATCH_FILES,
} from "../../api/UserDocumentApi";
import "./ManagerCreateUserView.scss";

const ManagerCreateUserView: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [middleName, setMiddleName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [driversLicense, setDriversLicense] = useState<string>("");
  const [licenseType, setLicenseType] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Documents to attach to the new client (Hannah, Jul 29 2026: intake usually arrives as a bundle —
  // citation, licence, insurance — so they are picked here and uploaded once the client record exists).
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadFailures, setUploadFailures] = useState<Array<{ name: string; error: string }>>([]);
  // Set once the client is created; only used when an upload problem keeps us on this page.
  const [createdUserGuid, setCreatedUserGuid] = useState<string | null>(null);

  const addFiles = (files: File[]) => {
    setError(null);
    setPendingFiles((current) => {
      // De-duplicate by name+size so picking twice does not upload the same file twice.
      const seen = new Set(current.map((f) => `${f.name}:${f.size}`));
      const merged = [...current, ...files.filter((f) => !seen.has(`${f.name}:${f.size}`))];
      if (merged.length > MAX_BATCH_FILES) {
        setError(`Attach up to ${MAX_BATCH_FILES} files. Extra selections were not added.`);
        return merged.slice(0, MAX_BATCH_FILES);
      }
      return merged;
    });
  };

  const removeFile = (index: number) => {
    setPendingFiles((current) => current.filter((_, i) => i !== index));
  };

  const createUserReq = async () => {
    setError(null);
    setUploadFailures([]);
    setLoading(true);

    try {
      // The client may already exist from an earlier attempt whose uploads failed — don't create twice.
      const userGuid =
        createdUserGuid ??
        (
          await ManagerApi.createUser({
            User_Email: email.trim() || undefined,
            User_FirstName: firstName,
            User_MiddleName: middleName || undefined,
            User_LastName: lastName,
            User_DOB: dob && dob.trim() !== '' ? dob : undefined,
            User_PhoneNumber: phoneNumber || undefined,
            User_DriversLicenseNumber: driversLicense || undefined,
            User_LicenseType: licenseType || undefined,
            User_Source: source || undefined
          })
        ).User_guid;
      setCreatedUserGuid(userGuid);

      if (pendingFiles.length > 0) {
        // The client exists at this point, so an upload problem must never read as "creation failed".
        // Any file the server refused is named, and we stay put so staff can decide what to do.
        try {
          const result = await UserDocumentApi.uploadDocuments(userGuid, pendingFiles);
          if (result.failedCount > 0) {
            setUploadFailures(result.failed);
            // Keep only the files that did not land, so the button retries exactly those and a
            // successful file is never uploaded a second time.
            const failedNames = new Set(result.failed.map((f) => f.name));
            setPendingFiles((current) => current.filter((f) => failedNames.has(f.name)));
            return;
          }
        } catch (uploadError) {
          console.error("Document upload failed after client creation", uploadError);
          setUploadFailures(
            pendingFiles.map((f) => ({
              name: f.name,
              error: uploadError instanceof Error ? uploadError.message : 'Upload failed',
            })),
          );
          return;
        }
      }

      // Navigate to the user's page in ManagerUserView
      navigate(`/user?User_guid=${userGuid}`);
    } catch (error) {
      console.error("User creation failed", error);
      // Say what the server actually said ("Email already exists", the failing field). The blanket
      // "check all fields" message sent staff hunting through a form that was usually correct —
      // Maria Elena, Jul 29. A TypeError here is the fetch itself failing, which has no useful
      // message of its own, so that case keeps the generic wording.
      const serverMessage = error instanceof Error && !(error instanceof TypeError)
        ? error.message.trim()
        : '';
      setError(
        serverMessage || "Failed to create user. Please check all fields and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Email and phone are both optional (Hannah, Aug 8 2026): clients arrive with one, the other, or
  // neither, and the record still has to be created. A value that IS typed must still be well
  // formed — optional means "may be left blank", not "may be wrong".
  const emailValid = () => {
    if (email.trim() === '') return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const firstNameValid = () => {
    return firstName.length > 0;
  };

  const lastNameValid = () => {
    return lastName.length > 0;
  };

  // Blank is allowed: staff frequently start a case without the client's date of birth. A date that
  // IS entered still has to be a real one in the past.
  const dobValid = () => {
    if (!dob || dob.trim() === '') return true;
    return !isNaN(Date.parse(dob)) && Date.parse(dob) < Date.now();
  };

  const phoneNumberValid = () => {
    if (!phoneNumber || phoneNumber.trim() === '') return true;
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)\.]/g, '');
    return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
  };

  const valid = () => {
    return (
      emailValid() && 
      firstNameValid() && 
      lastNameValid() && 
      dobValid() &&
      phoneNumberValid()
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && valid()) {
      createUserReq();
    }
  };

  return (
    <>
      <Container maxWidth="sm" className="manager-create-view">
        <Card className="manager-create-card">
          <div className="manager-create-header">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
              size="small"
            >
              Back
            </Button>
            <Typography variant="h4" className="manager-create-title">
              Create New Client
            </Typography>
          </div>

          {error && (
            <Alert severity="error" className="manager-create-error">
              {error}
            </Alert>
          )}

          <div className="manager-create-form">
            <TextField 
              disabled={loading}
              autoFocus
              value={email}
              error={email.length > 0 && !emailValid()}
              helperText={
                email.length > 0 && !emailValid()
                  ? "Please enter a valid email address"
                  : "Optional — without one this client receives no email. It can be added later."
              }
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Email"
              type="email"
              variant="outlined"
              fullWidth
            />
            
            <TextField
              disabled={loading}
              value={firstName}
              error={firstName.length > 0 && !firstNameValid()}
              helperText={firstName.length > 0 && !firstNameValid() ? "First name is required" : ""}
              required
              onChange={(e) => setFirstName(e.target.value)}
              onKeyPress={handleKeyPress}
              label="First Name"
              variant="outlined"
              fullWidth
            />
            
            <TextField
              disabled={loading}
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Middle Name"
              variant="outlined"
              fullWidth
              helperText="Middle name or initial (optional)"
            />
            
            <TextField
              disabled={loading}
              value={lastName}
              error={lastName.length > 0 && !lastNameValid()}
              helperText={lastName.length > 0 && !lastNameValid() ? "Last name is required" : ""}
              required
              onChange={(e) => setLastName(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Last Name"
              variant="outlined"
              fullWidth
            />
            
            <TextField
              disabled={loading}
              value={dob}
              error={dob.length > 0 && !dobValid()}
              helperText={dob.length > 0 && !dobValid() ? "Invalid date of birth" : "Optional"}
              onChange={(e) => setDob(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Date of Birth"
              variant="outlined"
              type="date"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
            
            <TextField
              disabled={loading}
              value={phoneNumber}
              error={phoneNumber.length > 0 && !phoneNumberValid()}
              helperText={
                phoneNumber.length > 0 && !phoneNumberValid()
                  ? "Invalid phone number format"
                  : "Optional — without one this client receives no text messages. It can be added later."
              }
              onChange={(e) => setPhoneNumber(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Phone Number"
              variant="outlined"
              type="tel"
              fullWidth
              placeholder="(555) 123-4567"
            />

            <TextField
              disabled={loading}
              value={driversLicense}
              onChange={(e) => setDriversLicense(e.target.value)}
              onKeyPress={handleKeyPress}
              label="Driver's License #"
              variant="outlined"
              fullWidth
              helperText="One per client (optional)"
            />

            <FormControl fullWidth disabled={loading}>
              <InputLabel id="license-type-label">License Type</InputLabel>
              <Select
                labelId="license-type-label"
                label="License Type"
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
              >
                <MenuItem value=""><em>Not specified</em></MenuItem>
                <MenuItem value="CDL">CDL</MenuItem>
                <MenuItem value="Regular">Regular</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading}>
              <InputLabel id="source-label">Source</InputLabel>
              <Select
                labelId="source-label"
                label="Source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <MenuItem value=""><em>Not specified</em></MenuItem>
                {SOURCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <div>
              <Button
                component="label"
                variant="outlined"
                fullWidth
                disabled={loading}
                startIcon={<UploadFileIcon />}
              >
                Attach Documents (optional)
                <input
                  type="file"
                  hidden
                  multiple
                  accept={ACCEPTED_DOCUMENT_TYPES}
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = '';
                    if (files.length > 0) addFiles(files);
                  }}
                />
              </Button>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                Citation, driver&apos;s licence, insurance, court notices — attach up to{' '}
                {MAX_BATCH_FILES} at once. They upload as soon as the client is created.
              </Typography>

              {pendingFiles.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {pendingFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${file.size}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <DescriptionIcon fontSize="small" color="action" />
                      <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-word' }}>
                        {file.name}
                      </Typography>
                      <Button
                        size="small"
                        color="inherit"
                        disabled={loading}
                        onClick={() => removeFile(index)}
                        aria-label={`Remove ${file.name}`}
                        startIcon={<CloseIcon fontSize="small" />}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {uploadFailures.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  The client was created, but {uploadFailures.length} file
                  {uploadFailures.length === 1 ? '' : 's'} could not be uploaded:
                </Typography>
                {uploadFailures.map((f) => (
                  <Typography key={f.name} variant="caption" sx={{ display: 'block' }}>
                    {f.name} — {f.error}
                  </Typography>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => navigate(`/user?User_guid=${createdUserGuid}`)}
                >
                  Continue to client file
                </Button>
              </Alert>
            )}

            <Button
              disabled={!valid() || loading}
              onClick={createUserReq}
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {loading
                ? "Creating Client..."
                : !createdUserGuid
                  ? "Create Client"
                  : pendingFiles.length > 0
                    ? "Retry Document Upload"
                    : "Continue to Client File"}
            </Button>
          </div>
        </Card>
      </Container>
    </>
  );
};

export default ManagerCreateUserView;
